// MobileTempLog — offline-first temperature log entry.
// Writes via syncQueue → POST /api/mobile/safety/temperature/log.
// On reconnect the queued payloads flush automatically.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Thermometer, ArrowLeft, CheckCircle2, Loader2
} from 'lucide-react';
import MobileOfflineBanner from './MobileOfflineBanner';
import { queueOrSend } from '../utils/syncQueue';
import { readSharedToken } from '../utils/authTokenKeys';
import { authHeaders } from '../../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const MobileTempLog = () => {
  const [equipmentId, setEquipmentId] = useState('');
  const [reading, setReading] = useState('');
  const [unit, setUnit] = useState('C');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [recent, setRecent] = useState([]);
  const [feedback, setFeedback] = useState(null);

  // Show actual site-scoped records only; equipment presets and pass/fail limits are not configured.
  useEffect(() => {
    // Was localStorage.getItem('token') || localStorage.getItem('mobileToken')
    // — never checked auth_token (audit-2026-09-07 §2, same bug syncQueue.js had).
    const token = readSharedToken();
    if (!token || !navigator.onLine || !localStorage.getItem('current_organization_id') || !localStorage.getItem('current_location_id')) return;
    fetch(`${API_URL}/api/mobile/safety/temperature`, {
      headers: authHeaders({ token, locationId: localStorage.getItem('current_location_id') || '' }),
    })
      .then(r => r.ok ? r.json() : [])
      .then((logs) => Array.isArray(logs) && setRecent(logs.slice(0, 10)))
      .catch(() => {});
  }, []);

  const value = parseFloat(reading);
  const submit = async () => {
    if (!equipmentId.trim() || reading === '' || !Number.isFinite(value)) return;
    setSubmitting(true);
    setFeedback(null);
    const res = await queueOrSend({
      method: 'POST',
      path: '/api/mobile/safety/temperature/log',
      requiresScope: true,
      queueType: 'safety',
      body: {
        equipmentId: equipmentId.trim(),
        temperature: value,
        unit,
        notes,
      },
    });
    setSubmitting(false);
    if (res.ok) {
      setFeedback({ kind: 'ok', msg: `Recorded ${value}°${unit} for ${equipmentId.trim()}. No safe-range assessment was performed.` });
    } else if (res.queued) {
      setFeedback({ kind: 'queued', msg: `Offline — saved locally, will sync when online (${equipmentId.trim()}: ${value}°${unit})` });
    } else {
      setFeedback({ kind: 'error', msg: res.error || 'Failed to log temperature' });
    }
    if (res.ok || res.queued) {
      setReading('');
      setNotes('');
    }
  };

  return (
    <div className="min-h-screen bg-surface-muted" data-testid="boh-temp-log-page">
      <MobileOfflineBanner />
      <header className="bg-surface border-b border-surface-border px-4 py-3 flex items-center gap-3">
        <Link to="/mobile/boh" className="p-2 -ml-2 rounded-md hover:bg-surface-sunken" data-testid="boh-temp-back">
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <div>
          <div className="text-xs uppercase tracking-overline text-ink-muted">Back of House</div>
          <h1 className="font-display text-lg text-ink flex items-center gap-2"><Thermometer className="w-4 h-4 text-primary-500" /> Temperature Log</h1>
        </div>
      </header>

      <div className="px-4 py-5 space-y-5 pb-24">
        {/* Equipment selector — large touch targets */}
        <div>
          <label htmlFor="boh-temp-equipment" className="text-xs uppercase tracking-overline text-ink-muted">Equipment or location</label>
          <input id="boh-temp-equipment" required maxLength="160" value={equipmentId} onChange={(event) => setEquipmentId(event.target.value)} placeholder="Enter the equipment you measured" className="mt-2 w-full px-3 py-3 bg-surface border border-surface-border text-sm" data-testid="boh-temp-equipment-input" />
        </div>

        {/* Reading entry */}
        <div className="bg-surface border border-surface-border rounded-lg p-4">
          <label htmlFor="boh-temp-reading-input" className="text-xs uppercase tracking-overline text-ink-muted">Measured temperature</label>
          <div className="mt-2 flex items-center gap-2">
            <input
              id="boh-temp-reading-input"
              type="number"
              step="0.1"
              inputMode="decimal"
              value={reading}
              onChange={(e) => setReading(e.target.value)}
              placeholder="0.0"
              className="min-w-0 flex-1 px-3 py-3 text-2xl font-data tabular-nums text-center bg-surface-muted border border-surface-border focus:outline-none focus:border-primary-500"
              data-testid="boh-temp-reading-input"
            />
            <select aria-label="Temperature unit" value={unit} onChange={(event) => setUnit(event.target.value)} className="h-12 border border-surface-border bg-surface px-3 text-sm">
              <option value="C">°C</option><option value="F">°F</option>
            </select>
          </div>
          <p className="mt-2 text-xs text-amber-800">This reading is recorded as reported. No configured limit or safety assessment is available.</p>

          <label className="text-xs uppercase tracking-overline text-ink-muted mt-4 block">Notes (optional)</label>
          <textarea aria-label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="mt-1 w-full px-3 py-2 bg-surface-muted border border-surface-border rounded-md text-sm focus:outline-none focus:border-primary-500"
            placeholder="e.g. door left open during delivery"
            data-testid="boh-temp-notes"
          />

          <button
            disabled={submitting || !equipmentId.trim() || reading === ''}
            onClick={submit}
            className="mt-4 w-full bg-primary-500 text-surface py-3 rounded-md text-sm font-medium hover:bg-primary-600 disabled:opacity-40 motion-organic"
            data-testid="boh-temp-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 inline mr-2 animate-spin" /> : null}
            Save reading
          </button>
        </div>

        {/* Feedback */}
        {feedback ? (
          <div
            className={`rounded-md px-3 py-2 text-sm flex items-start gap-2 ${
              feedback.kind === 'ok' ? 'bg-ok/10 text-ok'
              : feedback.kind === 'queued' ? 'bg-warn/10 text-warn'
              : 'bg-danger/10 text-danger'
            }`}
            data-testid="boh-temp-feedback"
          >
            <CheckCircle2 className="w-4 h-4 mt-0.5" /> {feedback.msg}
          </div>
        ) : null}

        {/* Recent (online-only) */}
        {recent.length > 0 ? (
          <div className="bg-surface border border-surface-border rounded-lg overflow-hidden">
            <div className="px-3 py-2 text-xs uppercase tracking-overline text-ink-muted border-b border-surface-border">Recent (last sync)</div>
            <ul>
              {recent.map((r, i) => (
                <li key={r.id || i} className="px-3 py-2 border-b border-surface-border last:border-0 flex items-center justify-between text-sm" data-testid={`boh-temp-recent-${i}`}>
                  <span className="text-ink">{r.equipment_id}</span>
                  <span className="font-data tabular-nums">{r.temperature}°{r.unit || 'C'}</span>
                  <span className="text-xs text-ink-muted">{r.timestamp ? new Date(r.timestamp).toLocaleTimeString() : ''}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default MobileTempLog;
